import { BROWSER_LOCAL_CACHE_DB_NAME, BROWSER_LOCAL_CACHE_DB_VERSION } from './config'
import { publishBrowserLocalCacheInvalidation } from './crossTab'
import type { BrowserLocalCacheDriver, BrowserLocalCacheRecord, BrowserLocalCacheStoreName } from './types'

type StoreDefinition = {
  keyPath: string
  indexes: string[]
}

const STORE_DEFINITIONS: Record<BrowserLocalCacheStoreName, StoreDefinition> = {
  cache_entries: {
    keyPath: 'cacheKey',
    indexes: ['userScope', 'route', 'entityType', 'sensitivity', 'encryptionVersion', 'expiresAt', 'updatedAt', 'lastAccessedAt'],
  },
  entities: {
    keyPath: 'entityKey',
    indexes: ['userScope', 'entityType', 'sensitivity', 'encryptionVersion', 'serverUpdatedAt', 'dirty', 'tombstone'],
  },
  task_center_items: {
    keyPath: 'taskKey',
    indexes: ['userScope', 'status', 'storeId', 'sensitivity', 'encryptionVersion', 'updatedAt', 'localUpdatedAt'],
  },
  images: {
    keyPath: 'imageKey',
    indexes: [
      'userScope',
      'canonicalUrl',
      'sourceType',
      'sensitivity',
      'encryptionVersion',
      'contentHash',
      'version',
      'cacheMode',
      'isBlobCacheEligible',
      'failureReason',
      'lastSeenAt',
      'lastAccessedAt',
      'byteSize',
    ],
  },
  sync_cursors: {
    keyPath: 'cursorKey',
    indexes: ['userScope', 'resourceName', 'lastSyncedAt', 'updatedAt'],
  },
  mutation_journal: {
    keyPath: 'mutationId',
    indexes: ['userScope', 'entityType', 'status', 'createdAt'],
  },
  crypto_metadata: {
    keyPath: 'userScope',
    indexes: ['keyId', 'algorithm', 'version', 'createdAt', 'rotatedAt'],
  },
  performance_budget: {
    keyPath: 'resourceName',
    indexes: ['userScope', 'updatedAt'],
  },
  cache_events: {
    keyPath: 'eventId',
    indexes: ['userScope', 'resourceName', 'createdAt', 'type'],
  },
}

const STORE_KEY_FIELDS: Record<BrowserLocalCacheStoreName, string> = Object.fromEntries(
  Object.entries(STORE_DEFINITIONS).map(([storeName, definition]) => [storeName, definition.keyPath])
) as Record<BrowserLocalCacheStoreName, string>

const openIndexedDb = (): Promise<IDBDatabase> => {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is unavailable'))
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(BROWSER_LOCAL_CACHE_DB_NAME, BROWSER_LOCAL_CACHE_DB_VERSION)
    request.onerror = () => reject(request.error || new Error('Failed to open browser local cache database'))
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = () => {
      const db = request.result
      for (const [storeName, definition] of Object.entries(STORE_DEFINITIONS) as Array<[BrowserLocalCacheStoreName, StoreDefinition]>) {
        const store = db.objectStoreNames.contains(storeName)
          ? request.transaction?.objectStore(storeName)
          : db.createObjectStore(storeName, { keyPath: definition.keyPath })
        if (!store) continue
        for (const indexName of definition.indexes) {
          if (!store.indexNames.contains(indexName)) {
            store.createIndex(indexName, indexName, { unique: false })
          }
        }
      }
    }
  })
}

const transactionDone = (transaction: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'))
    transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'))
  })

const requestResult = <TValue>(request: IDBRequest<TValue>): Promise<TValue> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'))
  })

const withStoreKey = <TPayload>(
  storeName: BrowserLocalCacheStoreName,
  key: string,
  record: BrowserLocalCacheRecord<TPayload>
): BrowserLocalCacheRecord<TPayload> & Record<string, unknown> => ({
  ...record,
  [STORE_KEY_FIELDS[storeName]]: key,
})

export class IndexedDbLocalCacheDriver implements BrowserLocalCacheDriver {
  private dbPromise: Promise<IDBDatabase> | null = null

  private async getDb(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openIndexedDb()
    }
    return await this.dbPromise
  }

  async get<TPayload = unknown>(
    storeName: BrowserLocalCacheStoreName,
    key: string
  ): Promise<BrowserLocalCacheRecord<TPayload> | null> {
    const db = await this.getDb()
    const transaction = db.transaction(storeName, 'readonly')
    const record = await requestResult(transaction.objectStore(storeName).get(key))
    await transactionDone(transaction)
    return (record as BrowserLocalCacheRecord<TPayload> | undefined) || null
  }

  async put<TPayload = unknown>(
    storeName: BrowserLocalCacheStoreName,
    key: string,
    record: BrowserLocalCacheRecord<TPayload>
  ): Promise<void> {
    const db = await this.getDb()
    const transaction = db.transaction(storeName, 'readwrite')
    transaction.objectStore(storeName).put(withStoreKey(storeName, key, record))
    await transactionDone(transaction)
  }

  async delete(storeName: BrowserLocalCacheStoreName, key: string): Promise<void> {
    const db = await this.getDb()
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const existing = await requestResult(store.get(key)) as BrowserLocalCacheRecord<unknown> | undefined
    store.delete(key)
    await transactionDone(transaction)
    publishBrowserLocalCacheInvalidation({
      storeName,
      cacheKey: key,
      userScope: existing?.userScope,
      resourceName: existing?.resourceName,
      reason: 'driver_delete',
    })
  }

  async listByUserScope<TPayload = unknown>(
    storeName: BrowserLocalCacheStoreName,
    userScope: string,
    limit = 500
  ): Promise<Array<BrowserLocalCacheRecord<TPayload>>> {
    const db = await this.getDb()
    const transaction = db.transaction(storeName, 'readonly')
    const index = transaction.objectStore(storeName).index('userScope')
    const request = index.openCursor(IDBKeyRange.only(userScope))
    const result: Array<BrowserLocalCacheRecord<TPayload>> = []
    await new Promise<void>((resolve, reject) => {
      request.onerror = () => reject(request.error || new Error('IndexedDB cursor failed'))
      request.onsuccess = () => {
        const cursor = request.result
        if (!cursor || result.length >= limit) {
          resolve()
          return
        }
        result.push(cursor.value as BrowserLocalCacheRecord<TPayload>)
        cursor.continue()
      }
    })
    await transactionDone(transaction)
    return result
  }

  async clearUserScope(storeName: BrowserLocalCacheStoreName, userScope: string): Promise<number> {
    const db = await this.getDb()
    const transaction = db.transaction(storeName, 'readwrite')
    const index = transaction.objectStore(storeName).index('userScope')
    const request = index.openCursor(IDBKeyRange.only(userScope))
    let removed = 0
    await new Promise<void>((resolve, reject) => {
      request.onerror = () => reject(request.error || new Error('IndexedDB cursor failed'))
      request.onsuccess = () => {
        const cursor = request.result
        if (!cursor) {
          resolve()
          return
        }
        cursor.delete()
        removed += 1
        cursor.continue()
      }
    })
    await transactionDone(transaction)
    if (removed > 0) {
      publishBrowserLocalCacheInvalidation({
        storeName,
        userScope,
        deletedCount: removed,
        reason: 'driver_clear_user_scope',
      })
    }
    return removed
  }
}

export const createIndexedDbLocalCacheDriver = (): IndexedDbLocalCacheDriver => new IndexedDbLocalCacheDriver()
